import { createEvent, EventAttributes } from 'ics';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import advancedFormat from 'dayjs/plugin/advancedFormat';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(duration);
dayjs.extend(relativeTime);
dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);

const WORDS_PER_MINUTE = 200;

export type ReadTime = {
	words: number;
	minutes: number;
	human: string;
};

export type TimezoneInfo = {
	name: string;
	offset: number;
	offsetString: string;
};

export type ParsableDate = string | number | Date | Dayjs | null | undefined;
export default class DateTimeUtils {
	public static parseTz(date: ParsableDate, timezone: string, format: string): Date {
		const asDayJs = dayjs.tz(date, format, timezone);
		return asDayJs.toDate();
	}
	public static parseUtc(date: ParsableDate, format?: string): Date {
		const asDayJs = dayjs.utc(date, format);
		return asDayJs.toDate();
	}

	public static parse(date: ParsableDate, format?: string): Date {
		const asDayJs = dayjs(date, format);
		return asDayJs.toDate();
	}

	public static getTimezones() {
		return Intl.supportedValuesOf('timeZone');
	}

	public static getTimezoneInfo(): TimezoneInfo {
		const now = dayjs();
		const name = dayjs.tz.guess();
		const offset = now.utcOffset();

		return {
			name,
			offset,
			offsetString: 'GMT' + now.format('Z'),
		};
	}

	public static calculateReadTime(text: string): ReadTime {
		const words = text.split(/\s+/).length;
		const minutes = words / WORDS_PER_MINUTE;
		const human = DateTimeUtils.humanizeDuration(minutes, 'minutes');

		return {
			words,
			minutes,
			human,
		};
	}

	public static humanizeDuration(time: number, unit?: duration.DurationUnitType | undefined): string {
		return dayjs.duration(time, unit).humanize();
	}

	public static createICal(options: EventAttributes, filename: string) {
		return new Promise<File>((resolve, reject) => {
			createEvent(options, (error, value) => {
				if (error) {
					reject(error);
				} else {
					resolve(new File([value], filename, { type: 'text/calendar' }));
				}
			});
		});
	}

	public static format(date: ParsableDate, format: string, convertToTimezone?: string): string {
		const asDayJs = convertToTimezone ? dayjs(date).tz(convertToTimezone) : dayjs(date);
		return asDayJs.format(format);
	}
}

export const DATE_FORMAT = 'DD-MM-YYYY';
export const DATE_TIME_FORMAT = 'DD-MM-YYYY hh:mm A';
export const DATE_HUMAN_FORMAT = 'Do [of] MMM YYYY';
export const TIME_HUMAN_FORMAT = 'h:mm a';
export const DATE_TIME_SHORT_FORMAT = 'Do MMM @ h:mm a';
